import { IValidationJSONSchemaMessages } from '@estee/elc-form';
import { computed, observable } from 'mobx';
import { FIELD_TYPE } from '../constants/Constants';
import { translationKeysFieldMappings, disabledFieldsByFeature } from '../constants/AddressFieldsStaticDefinitions';
import { IAddressSchemaProperty, ISchemaAdditionalProperties } from '../api/JsonSchemaApi';
import { FEATURE } from '../constants/Features';
import { ITranslationSchema } from '../interfaces/ITranslationFields';

interface IInputFieldDataConfig {
  fieldType: FIELD_TYPE;
  translations: ITranslationSchema;
  propertySchema: IAddressSchemaProperty;
  features: FEATURE[];
  isRequired: boolean;
}

export class InputFieldData {
  @observable config: IInputFieldDataConfig;

  constructor(config: IInputFieldDataConfig) {
    this.config = config;
  }

  @computed get validationMessages(): IValidationJSONSchemaMessages {
    const { fieldType, translations } = this.config;
    // translation key names by field property name
    const keyNames = translationKeysFieldMappings[fieldType];
    const invalidTranslationKey = this.properties?.invalidKey ?? keyNames?.invalid;
    const maxLengthMessage = keyNames && translations[keyNames.maxLength] as string;
    const minLengthMessage = keyNames && translations[keyNames.minLength] as string;
    const patternMessage = invalidTranslationKey && translations[invalidTranslationKey] as string;
    const requiredMessage = keyNames && translations[keyNames.required] as string;

    return {
      maxLengthMessage,
      minLengthMessage,
      patternMessage,
      requiredMessage,
    };
  }

  @computed get label(): string {
    const { fieldType, translations } = this.config;
    const keyNames = translationKeysFieldMappings[fieldType];
    let translatedLabel;

    if (this.labelKey) {
      translatedLabel = translations[this.labelKey];
    }
    if (!translatedLabel) {
      if (keyNames) {
        translatedLabel = translations[keyNames.label];
      } else {
        translatedLabel = `${fieldType}`;
      }
    }

    return translatedLabel as string;
  }

  @computed get name(): string {
    const { fieldType } = this.config;

    return fieldType;
  }

  @computed get autocomplete(): string {
    return this.properties?.autofill;
  }

  @computed get labelKey(): string {
    return this.properties?.labelKey;
  }

  @computed get isRequired(): boolean {
    const { isRequired } = this.config;

    return isRequired;
  }

  @computed get inputType(): string {
    return this.properties?.input_type;
  }

  @computed get properties(): ISchemaAdditionalProperties {
    return this.config.propertySchema?.additionalProperties;
  }

  @computed get isDisabled(): boolean {
    const { features, fieldType } = this.config;

    return features.reduce(
      (acc, feature) => acc || disabledFieldsByFeature[feature]?.includes(fieldType), false,
    );
  }
}
